import type { Timestamp } from "@firebase/firestore-types";
import type { Account } from "../account/models.js";

export type SwanKybFormField =
  | "companyName"
  | "registrationNumber"
  | "legalForm"
  | "headOfficeAddress"
  | "businessActivity"
  | "paymentVolume"
  | "vatNumber"
  | "taxId"
  | "legalRepresentative"
  | "beneficialOwners"
  | "other";

export const swanKybRequirements = [
  "FirstTransferRequired",
  "SupportingDocumentsRequired",
  "UboDetailsRequired",
  "LegalRepresentativeDetailsRequired",
  "OrganizationDetailsRequired",
  "TaxIdRequired",
  "Other",
] as const;
export type SwanKybRequirement = (typeof swanKybRequirements)[number];

export const swanKybDocumentPurposes = [
  "AssociationRegistration",
  "Banking",
  "CompanyRegistration",
  "GeneralAssemblyMinutes",
  "PowerOfAttorney",
  "ProofOfBusinessActivity",
  "ProofOfCompanyAddress",
  "ProofOfCompanyIncome",
  "ProofOfIdentity",
  "ProofOfIndividualAddress",
  "ProofOfIndividualIncome",
  "ProofOfOriginOfFunds",
  "SignedStatus",
  "UBODeclaration",
  "USPersonStatusDeclaration",
  "SwornStatement",
  "LegalRepresentativeProofOfIdentity",
  "UltimateBeneficialOwnerProofOfIdentity",
  "UltimateBeneficialOwnerProofOfAddress",
  "NIFAccreditationCard",
  "PresidentDecisionOfAppointment",
  "AdministratorDecisionOfAppointment",
  "FinancialStatements",
  "PepDeclaration",
  "CompanyFormationRegistration",
  "Other",
] as const;
export type SwanKybDocumentPurpose = (typeof swanKybDocumentPurposes)[number];

export const swanKybDocumentTypes = [
  "Selfie",
  "Passport",
  "NationalIdCard",
  "ResidentPermit",
  "DrivingLicense",
  "UtilityBill",
  "PhoneBill",
  "RentReceipt",
  "HomeInsurance",
  "IncomeTaxReturn",
  "PaySlip",
  "RegisterExtract",
  "ArticlesOfIncorporation",
  "CapitalShareDepositCertificate",
  "UBODeclaration",
  "JOAFFEExtract",
  "CompanyLeaseAgreement",
  "BankStatement",
  "BankAccountDetails",
  "PowerOfAttorney",
  "CorporateIncomeTaxReturn",
  "Other",
  "ByLaws",
  "AccountStatement",
  "DeedOfDonation",
  "DeedOfSale",
  "DeedOfSuccession",
  "LoanContract",
  "NotarialDeed",
  "SwornStatement",
  "MeetingMinutes",
  "NIFAccreditationCard",
  "DecisionOfAppointment",
  "FinancialStatements",
  "WinningsCertificate",
  "Invoice",
  "PepDeclaration",
  "W9",
  "W8",
  "CertificateOfLossOfNationality",
] as const;
export type SwanKybDocumentType = (typeof swanKybDocumentTypes)[number];

export const swanKybRefusalReasons = [
  "CompanyNameMismatch",
  "ExpiredDocument",
  "InvalidAddress",
  "InvalidDocument",
  "InvalidOrMissingData",
  "UnacceptableDocument",
  "ProviderColorIdDocumentRequired",
  "MissingSignature",
  "BadDocumentQuality",
  "FullDocumentRequired",
  "Other",
] as const;
export type SwanKybRefusalReason = (typeof swanKybRefusalReasons)[number];

export type SwanKybVerificationStatus =
  "NotStarted" | "WaitingForInformation" | "Pending" | "Verified" | "Refused";

export type SwanKybDocumentStatus = "missing" | "inReview" | "validated" | "refused";

export type SwanKybDocumentSwanStatus =
  "WaitingForUpload" | "NotUploaded" | "Uploaded" | "Validated" | "Refused";

export type SwanKybCollectionStatus =
  "WaitingForDocument" | "PendingReview" | "Approved" | "Canceled" | "Rejected";

/**
 * One line per document purpose Swan asks for, from its latest document. Unknown Swan values
 * fall back to `Other`; `swanPurpose` / `swanReasonCode` keep the raw value.
 */
export type SwanKybDocument = {
  purpose: SwanKybDocumentPurpose;
  swanPurpose: string;
  /** When the document reached `status` (requested, uploaded, validated or refused). */
  since: Timestamp;
  swanStatus?: SwanKybDocumentSwanStatus | null;
  acceptableTypes?: string[];
  purposeDetails?: string | null;
} & (
  | { status: "missing" | "inReview" | "validated" }
  | { status: "refused"; reason: SwanKybRefusalReason; swanReasonCode: string }
);

/**
 * Photo of a Swan KYB, kept by the Swan webhooks while the account has no IBAN. Holds no
 * personal data: field paths and document purposes only (no addresses, names or files).
 */
export type SwanKyb = {
  /** Account holder id once it exists, else the onboarding id: what Swan support quotes. */
  reference: string;
  /** Swan portal page for this document collection. Null until Swan has created one. */
  collectionUrl: string | null;
  collectionStatus?: SwanKybCollectionStatus | null;
  companyName: string | null;
  registrationNumber: string | null;
  form: {
    status: "Invalid" | "Valid" | "Finalized";
    errors: { field: string; category: SwanKybFormField; codes: ("Missing" | "Invalid")[] }[];
  };
  verification: {
    status: SwanKybVerificationStatus;
    since: Timestamp | null;
    requirements: SwanKybRequirement[];
    /** Swan free text, only when refused. */
    refusalReason: string | null;
  };
  documents: SwanKybDocument[];
};

export type SwanKybFormState = "inProgress" | "blocked" | "finished";

export const getSwanKybFormState = (form: SwanKyb["form"]): SwanKybFormState => {
  if (form.status === "Finalized") return "finished";
  if (form.errors.some(({ codes }) => codes.includes("Invalid"))) return "blocked";
  return "inProgress";
};

export type SwanKybStage =
  | { stage: "formInProgress" }
  | { stage: "formBlocked" }
  | { stage: "verificationNotStarted" }
  | {
      stage: "waitingForDocuments";
      purpose: SwanKybDocumentPurpose;
      count: number;
      since: Timestamp;
    }
  | { stage: "waitingForInformation"; requirement: SwanKybRequirement; since: Timestamp | null }
  | { stage: "inReview"; since: Timestamp | null }
  | { stage: "refused" }
  | { stage: "accountWithoutIban" };

/** Where a Swan KYB stands, for a Swan account whose account has no IBAN yet. */
export const getSwanKybStage = (account: Account): SwanKybStage | undefined => {
  const kyb = account.swan_kyb;
  if (!kyb || account.swan_account?.IBAN) return undefined;

  switch (kyb.verification.status) {
    case "Refused":
      return { stage: "refused" };
    case "Verified":
      return { stage: "accountWithoutIban" };
    case "NotStarted":
    case "WaitingForInformation":
    case "Pending":
      break;
  }

  switch (getSwanKybFormState(kyb.form)) {
    case "inProgress":
      return { stage: "formInProgress" };
    case "blocked":
      return { stage: "formBlocked" };
    case "finished":
      break;
  }

  const waitingDocuments = kyb.documents
    .filter(({ status }) => status === "missing" || status === "refused")
    .sort((a, b) => a.since.toMillis() - b.since.toMillis());
  const [oldestWaitingDocument] = waitingDocuments;
  if (oldestWaitingDocument && kyb.verification.status !== "Pending")
    return {
      stage: "waitingForDocuments",
      purpose: oldestWaitingDocument.purpose,
      count: waitingDocuments.length,
      since: oldestWaitingDocument.since,
    };

  switch (kyb.verification.status) {
    case "NotStarted":
      return { stage: "verificationNotStarted" };
    case "Pending":
      return { stage: "inReview", since: kyb.verification.since };
    case "WaitingForInformation":
      return {
        stage: "waitingForInformation",
        requirement:
          kyb.verification.requirements.find(
            (requirement) => requirement !== "SupportingDocumentsRequired",
          ) ??
          kyb.verification.requirements[0] ??
          "Other",
        since: kyb.verification.since,
      };
  }
};
